/**
 * Replaces the `tokens` object previously provided by @chainsafe/web3-context.
 * Returns a map of { [tokenAddress]: { balance, decimals, symbol } } for all
 * tokens on the current home chain using wagmi's useReadContracts.
 */
import { useReadContracts } from "wagmi";
import { formatUnits } from "viem";
import { useAccount } from "wagmi";
import type { TokenConfig } from "../chainbridgeConfig";

const erc20Abi = [
  {
    type: "function" as const,
    name: "balanceOf",
    inputs: [{ name: "account", type: "address" as const }],
    outputs: [{ type: "uint256" as const }],
    stateMutability: "view" as const,
  },
  {
    type: "function" as const,
    name: "decimals",
    inputs: [],
    outputs: [{ type: "uint8" as const }],
    stateMutability: "view" as const,
  },
  {
    type: "function" as const,
    name: "symbol",
    inputs: [],
    outputs: [{ type: "string" as const }],
    stateMutability: "view" as const,
  },
] as const;

export type TokensMap = Record<
  string,
  { balance: number; decimals: number; symbol: string } | undefined
>;

export function useTokenBalances(tokenConfigs: TokenConfig[]): TokensMap {
  const { address } = useAccount();

  const contracts = tokenConfigs.flatMap((token) => [
    {
      address: token.address as `0x${string}`,
      abi: erc20Abi,
      functionName: "balanceOf" as const,
      args: [address ?? "0x0000000000000000000000000000000000000000"],
    },
    {
      address: token.address as `0x${string}`,
      abi: erc20Abi,
      functionName: "decimals" as const,
    },
    {
      address: token.address as `0x${string}`,
      abi: erc20Abi,
      functionName: "symbol" as const,
    },
  ]);

  const { data } = useReadContracts({
    contracts,
    query: { enabled: !!address && tokenConfigs.length > 0 },
  });

  return tokenConfigs.reduce<TokensMap>((acc, token, i) => {
    const rawBalance = (data?.[i * 3]?.result as bigint) ?? 0n;
    const decimals = (data?.[i * 3 + 1]?.result as number) ?? 18;
    const symbol = (data?.[i * 3 + 2]?.result as string) ?? token.symbol ?? "";
    const balance = parseFloat(formatUnits(rawBalance, decimals));
    return { ...acc, [token.address]: { balance, decimals, symbol } };
  }, {});
}
